/**
 * One status vocabulary for every scan surface (DEC-191 decision 1).
 *
 * Before DEC-191 the sidebar and the Past evaluations list each printed the system word
 * ("complete", "partial", "queued") in two different colour schemes. Every surface that
 * names a scan's status reads it from here: the dashboard rows, tiles and chart, and the
 * sidebar's live badge. Colour always comes with words, never alone.
 *
 * Colours are app theme tokens, so both themes follow without a palette of their own.
 */
import type { ScanState, ScanStatusGroup, ScanStatusResponse } from '../../types/api'
import type { PillTone } from '../shared/Badge'

export type ScanStatusKey =
  | 'running'
  | 'paused'
  | 'queued'
  | 'scheduled'
  | 'partial'
  | 'failed'
  | 'complete'
  | 'stopped'

export interface ScanStatusStyle {
  /** The word shown everywhere ("Finished with gaps"). */
  word: string
  /** The short word under a chart bar ("With gaps"). */
  short: string
  /** Which of the four dashboard numbers it counts in. */
  group: ScanStatusGroup
  /** The pill and dot tone (DEC-193 decision 2, one pill style). */
  tone: PillTone
  /** Text colour of the word when it stands alone. */
  text: string
  pulse: boolean
}

export const SCAN_STATUS: Record<ScanStatusKey, ScanStatusStyle> = {
  running: { word: 'Running', short: 'Running', group: 'in_progress', tone: 'running', text: 'text-accent-text', pulse: true },
  paused: { word: 'Paused', short: 'Paused', group: 'in_progress', tone: 'warn', text: 'text-warn', pulse: false },
  queued: { word: 'Waiting to start', short: 'Waiting', group: 'waiting', tone: 'waiting', text: 'text-body', pulse: false },
  scheduled: { word: 'Scheduled', short: 'Scheduled', group: 'waiting', tone: 'scheduled', text: 'text-body', pulse: false },
  partial: { word: 'Finished with gaps', short: 'With gaps', group: 'needs_a_look', tone: 'warn', text: 'text-warn', pulse: false },
  failed: { word: 'Failed', short: 'Failed', group: 'needs_a_look', tone: 'critical', text: 'text-critical', pulse: false },
  complete: { word: 'Finished', short: 'Finished', group: 'finished', tone: 'success', text: 'text-success', pulse: false },
  stopped: { word: 'Stopped by you', short: 'Stopped', group: 'finished', tone: 'stopped', text: 'text-body', pulse: false },
}

/** The four dashboard numbers, in order, with their labels. */
export const STATUS_GROUPS: { id: ScanStatusGroup; label: string; dotKey: ScanStatusKey }[] = [
  { id: 'in_progress', label: 'In progress now', dotKey: 'running' },
  { id: 'waiting', label: 'Waiting to start', dotKey: 'queued' },
  { id: 'needs_a_look', label: 'Needs a look', dotKey: 'failed' },
  { id: 'finished', label: 'Finished', dotKey: 'complete' },
]

const RUNNING_STATES: ScanState[] = ['cloning', 'planning', 'running', 'critic']

/** A scan row's status. A Stop is saved as `failed` with its own code (DEC-171), and it
 *  reads as the choice it was, never as a fault. */
export function statusOf(scan: Pick<ScanStatusResponse, 'state' | 'failure_code'>): ScanStatusKey {
  if (RUNNING_STATES.includes(scan.state)) return 'running'
  if (scan.state === 'paused') return 'paused'
  if (scan.state === 'queued') return 'queued'
  if (scan.state === 'partial') return 'partial'
  if (scan.state === 'complete') return 'complete'
  if (scan.state === 'failed') {
    return scan.failure_code === 'scan_stopped_by_user' ? 'stopped' : 'failed'
  }
  return 'queued'
}

/** A deferred request waits for capacity ("Waiting to start") or for a clock
 *  ("Scheduled"). A boundary within the next minute is "now" for a reader. */
export function pendingStatus(runAfter: string, now = Date.now()): ScanStatusKey {
  const t = new Date(runAfter).getTime()
  return Number.isFinite(t) && t - now > 60_000 ? 'scheduled' : 'queued'
}

/** The sidebar badge: "2 running · 1 waiting". Null when nothing is live. */
export function liveBadgeText(counts: { running: number; paused: number; queued: number; pending: number }): string | null {
  const parts: string[] = []
  if (counts.running) parts.push(`${counts.running} running`)
  if (counts.paused) parts.push(`${counts.paused} paused`)
  const waiting = counts.queued + counts.pending
  if (waiting) parts.push(`${waiting} waiting`)
  return parts.length ? parts.join(' · ') : null
}
